/** biome-ignore-all lint/a11y/noStaticElementInteractions: <explanation> */
import { show } from "@ebay/nice-modal-react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { canv } from "@/canv/global";
import { pasteEmbed } from "@/canvasController/helpers";
import { FILE_FORMATS_READABLE } from "@/constants";
import { eventEmitter } from "@/eventEmitter";
import { CreateEmbedModal } from "@/interface/ui/modals";
import { BuyProModal } from "@/interface/ui/modals/BuyProModal";
import { Tooltip } from "@/interface/ui/Tooltip";
import { VStack } from "@/shared/ui/Stack";
import type { Tool, ToolName } from "@/tools/types";
import InfoSvg from "../../../icons/Info.svg?react";
import { getMoreToolsNames, isBuyProLockedTool } from "../utils";
import styles from "./MoreWindow.module.css";

export const MoreWindow = () => {
    const { t } = useTranslation();
    const toolsNames = getMoreToolsNames();

    const handleSelect = (tool: Tool) => {
        if (isBuyProLockedTool(tool.name as ToolName)) {
            show(BuyProModal, {
                feature: tool.name === "commentTool" ? "comments" : "presentation",
            });
        } else if (tool.name === "stickersTool") {
            canv.toolBox.TryGetToolByName("stickersTool").select();
        } else if (tool.name === "embedTool") {
            show(CreateEmbedModal).then((embedUrl) => {
                if (!embedUrl) {
                    return;
                }
                pasteEmbed(embedUrl as string);
            });
        } else {
            canv.toolBox.switchToByName(tool.name as ToolName);
        }

        eventEmitter.dispatch("moreTool.visibility", { isVisible: false });
    };

    return (
        <div className={styles.moreWindow}>
            <VStack align="start" className={styles.moreWindowContent}>
                {toolsNames.map((toolName) => {
                    const tool = canv.toolBox.TryGetToolByName(toolName);
                    const isBuyProLocked = isBuyProLockedTool(toolName);

                    return (
                        <div
                            key={toolName}
                            className={cx(styles.moreWindowContentItem, {
                                [styles.moreWindowContentItemLocked]: isBuyProLocked,
                            })}
                            onClick={() => handleSelect(tool)}
                        >
                            <div
                                className={styles.moreWindowContentItemIcon}
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                                dangerouslySetInnerHTML={{ __html: tool.getToolView() }}
                            />
                            <div className={styles.moreWindowContentItemTitle}>
                                {tool.getToolTipName()}
                            </div>
                            {tool.getToolName() === "mediaTool" && (
                                <Tooltip
                                    content={t("more.mediaHint", {
                                        types: FILE_FORMATS_READABLE.join(", ").toUpperCase(),
                                    })}
                                    placement="right-start"
                                >
                                    <div className={styles.moreWindowContentItemInfo}>
                                        <InfoSvg />
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    );
                })}
            </VStack>
        </div>
    );
};
